import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import axios from 'axios';

import { SdfAlert } from '@synerg/react-components';
import { TemplateManager } from 'shared-one-ux-components';

import { figureOutCategories } from '../common';

export default function TemplateManagement() {
	const [templates, setTemplates] = useState([]);
	const [b2bScopes, setB2BScopes] = useState([]);
	const [b2bCategories, setb2bCategories] = useState([]);
	const [b2cScopes, setB2CScopes] = useState([]);
	const [b2cCategories, setb2cCategories] = useState([]);
	const [consents, setConsents] = useState([]);
	const [products, setProducts] = useState([]);
	const [categories, setCategories] = useState([]);
	const [templateNamesList, setTemplateNamesList] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [templateSuccess, setTemplateSuccess] = useState({ templateName: '', isSuccessful: false, status: '' });
	const [templateErrorMessage, setTemplateErrorMessage] = useState('');

	const fetchData = async () => {
		// doing this in parallel to speed things up
		setIsLoading(true);
		const promises = [];
		promises.push(axios(`${import.meta.env.VITE_ROOT_PATH}/api/scopes?grantType=clientCredentials`));
		promises.push(axios(`${import.meta.env.VITE_ROOT_PATH}/api/scopes?grantType=authorizationCode`));
		promises.push(axios(`${import.meta.env.VITE_ROOT_PATH}/api/consents`));
		promises.push(axios(`${import.meta.env.VITE_ROOT_PATH}/api/application/templates`));
		promises.push(axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/products`, { params: { dropdownSelection: true, includeDataCloud: true } }));
		promises.push(axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/template-categories`));
		Promise.all(promises).then(results => {
			setB2BScopes(results[0].data);
			setB2CScopes(results[1].data);
			setConsents(results[2].data);
			setTemplates(results[3].data);
			setProducts([...results[4].data]);
			setCategories([...results[5].data]);
			setIsLoading(false);

			const namesList = results[3].data.map((t) => ({
				name: t.templateName, sor: t.targetSORs[0].codeValue
			}));
			setTemplateNamesList(namesList);
			setb2bCategories(figureOutCategories(results[0].data));
			setb2cCategories(figureOutCategories(results[1].data));
		}).catch(e => {
			const msg = 'Something went wrong when trying to get application templates. Please try again.';
			setIsLoading(false);
			setTemplateErrorMessage(msg);
		})
	};

	const updateTemplate = async (data) => {
		setIsLoading(true);

		const isB2b = get(data, 'b2bApplicationScopes', []).length > 0;
		const isB2c = get(data, 'b2cApplicationScopes', []).length > 0;
		const isHybrid = isB2b && isB2c;

		const applicationTemplateID = get(data, 'applicationTemplateID');
		const formattedData = {
			...data,
			applicationCreationType: isHybrid ? 'B2B_B2C' : isB2b ? 'B2B' : 'B2C',
			targetSOR: data.targetSORs[0].codeValue,
			hideIndicator: !data.hideIndicator
		};

		try {
			const url = `${import.meta.env.VITE_ROOT_PATH}/api/application/templates/${applicationTemplateID}`
			await axios.post(url, formattedData);
			const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/application/templates`);
			setTemplates(data);
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Error Saving Template');
			setTemplateErrorMessage(errorMessage);
		}
		setIsLoading(false);
	};

	const onSumbitButton = async (formData, appTemplateID) => {
		const url = `${import.meta.env.VITE_ROOT_PATH}/api/application/templates`
			.concat(appTemplateID ? `/${appTemplateID}` : '');
		return await axios.post(url, formData);
	};

	const onDelete = async (applicationTemplateID) => {
		const url = `${import.meta.env.VITE_ROOT_PATH}/api/application/templates/delete`
			.concat(applicationTemplateID ? `/${applicationTemplateID}` : '');
		return await axios.post(url);
	};

	const retrieveTestOrgStatus = async (testOrgOid) => {
		const response = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/organization/${testOrgOid}/name`);
		const result = response.data;
		return result
	};

	useEffect(() => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div>
			{templateErrorMessage &&
				<SdfAlert closeable slot="alert" toast status="error" onSdfDismiss={() => setTemplateErrorMessage('')}>
					{templateErrorMessage}
				</SdfAlert>
			}
			{templateSuccess.isSuccessful &&
				<SdfAlert status="success" toast autoClose={true} onSdfCloseComplete={() => {
					setTemplateSuccess({ templateName: '', isSuccessful: false, status: '' });
				}}>
					Success! {templateSuccess.templateName} template {templateSuccess.status}.
				</SdfAlert>
			}
			<TemplateManager
				showLocale={true}
				data={templates}
				isPartnerApp={true}
				{...{
					fetchData, products, isLoading, updateTemplate, categories, b2bCategories, b2cCategories,
					b2bScopes, b2cScopes, templateNamesList, setTemplateSuccess, onDelete, onSumbitButton,
					retrieveTestOrgStatus, consents
				}}
			/>
		</div>
	);
}
